import { useNavigate } from 'react-router-dom';

import { INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { useAuthWithIntakeStatus } from 'hooks/useAuthWithIntakeStatus';
import { setCachedFlowCompletionStatus } from 'hooks/useCachedFlowCompletionStatus';
import { Icon } from '../icons/Icon';
import { Button } from '../ui/button';
import { Card, CardTitle } from '../ui/card';

const listItems = [
  {
    text: "Step 1) Learn more about how you think, react, and grow. ",
  },
  {
    text: "Step 2) Build a toolkit for healing, growth, and high performance. ",
  },
  {
    text: "Step 3) Strengthen how you support and uplift others!",
  },
];

export interface IntakeAssessmentCompleteProps {
  user: ReturnType<typeof useAuthWithIntakeStatus>['user'];
  updateUser: ReturnType<typeof useAuthWithIntakeStatus>['updateUser'];
  refetchIntakeStatus: ReturnType<typeof useAuthWithIntakeStatus>['refetchIntakeStatus'];
  intakeSyncRef: React.MutableRefObject<boolean>;
}

export const IntakeAssessmentComplete: React.FC<IntakeAssessmentCompleteProps> = ({
  user,
  updateUser,
  refetchIntakeStatus,
  intakeSyncRef,
}) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    // Update intake completion status when user leaves the completion screen
    if (!intakeSyncRef.current) {
      intakeSyncRef.current = true;
      updateUser({ has_completed_intake: true });

      const cacheKeySuffix = user?.id != null ? String(user.id) : undefined;
      setCachedFlowCompletionStatus(
        INTAKE_ASSESSMENT_FLOW_NAME,
        {
          completed_at: new Date().toISOString(),
          flow_name: INTAKE_ASSESSMENT_FLOW_NAME,
        },
        cacheKeySuffix,
        'system-intake',
      );

      void refetchIntakeStatus().catch(() => undefined);
    }

    navigate('/home');
  };

  return (
    <main className="flex h-full w-full items-center justify-center px-4">
      <Card maxWidth="large" padding="large" className="flex flex-col gap-8" data-flow-surface="card">
        <CardTitle title="Your journey will strengthen different Pillars over time." size="big" />
        <p className="type-body-m">
          Your journey begins with experiences designed to help you grow across different areas of life.
        </p>
        <ul className="inline-flex flex-col items-start gap-2">
          {listItems.map((item, index) => (
            <li key={index} className="inline-flex flex-col items-start gap-4">
              <div className="inline-flex items-center gap-2">
                <Icon name="orangeCheckmark2" size={20} />
                <span className="mt-[-1.00px] type-body-m text-foundation-base-darkbase-dark-500 text-left whitespace-normal break-words">
                  {item.text}
                </span>
              </div>
            </li>
          ))}
        </ul>
        <p className="self-stretch type-body-m text-foundation-base-darkbase-dark-500">
          Along the way, you&apos;ll gain insights and practical tools to help you grow and support others. Some experiences strengthen multiple pillars at once, while others deepen growth in specific areas. Over time, each step strengthens your Whole Human system.
        </p>
        <Button
          variant="default"
          onClick={handleGoHome}
          size="auto"
          className="self-center px-8"
        >
          Let&apos;s Begin
        </Button>
      </Card>
    </main>
  );
};
